import GradingIcon from "@mui/icons-material/Grading";
import ButtonGroup from "@mui/material/ButtonGroup";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useRef } from "react";
import type { ProposalChangeDescriptor } from "work/proposals/negotiation/proposal-change-description";
import { paneIsCompact } from "work/_views/work-pane-layout";
import { ProposalActionButton } from "work/proposals/_views/_shared/proposal-styled-components";
import ProposalChangeSummaryList from "work/proposals/_views/_shared/proposal-change-summary-list";

const SplitButtonContainer = styled("span")(({ theme }) => ({
  flexShrink: 0,
  maxWidth: "22rem",
  minWidth: theme.spacing(24),
  position: "relative",
  width: "100%",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
    minWidth: 0,
    width: "100%",
  },
}));
const SubmitReviewActionButton = styled(ProposalActionButton)(({ theme }) => ({
  lineHeight: 1.2,
  minHeight: theme.spacing(5.75),
  textAlign: "center",
  whiteSpace: "normal",
  "& .MuiButton-startIcon": {
    flexShrink: 0,
  },
}));
const COMPLETE_REVIEW_LABEL = "Complete Review";
const REVIEW_COMPLETED_LABEL = "Review Completed";
const SAVE_AND_COMPLETE_LABEL = "Save & Complete Review";

type Props = {
  className?: string;
  hasPendingChanges: boolean;
  canEdit?: boolean;
  disabled?: boolean;
  finalized?: boolean;
  loading?: boolean;
  size?: "small" | "medium" | "large";
  pendingChangeSummary?: {
    heading: string;
    descriptors: readonly ProposalChangeDescriptor[];
  };
  onFinalize: () => void;
  onSubmitAllAndFinalize: () => void;
};

type ButtonLabelArgs = {
  finalized?: boolean;
  hasPendingChanges: boolean;
};

/**
 * The button used to speak for comments too — "Review Completed | Save Comments" when the reviewer was
 * still holding one, because completing a review without a save left their rationale unsent. A comment
 * is written when the reviewer writes it, so the button is about the reviewer's proposal changes and
 * about completing the review, and about nothing else.
 */
function getButtonLabel(args: ButtonLabelArgs) {
  const { finalized, hasPendingChanges } = args;

  if (finalized) {
    return REVIEW_COMPLETED_LABEL;
  }
  if (hasPendingChanges) {
    return SAVE_AND_COMPLETE_LABEL;
  }
  return COMPLETE_REVIEW_LABEL;
}

function renderButtonLabel(label: string) {
  return label;
}

/**
 * What OK will actually do, in the reviewer's terms. Total by construction: with nothing pending,
 * completing the review is the whole of it, so there is no combination that leaves the dialog with no
 * sentence at all.
 */
function getFinalizeAction(args: {
  submitPendingItems: boolean;
  hasPendingChanges: boolean;
  changesAreItemized: boolean;
}) {
  const { submitPendingItems, hasPendingChanges, changesAreItemized } = args;

  if (submitPendingItems && hasPendingChanges) {
    if (changesAreItemized) {
      return "This will save the changes below and mark your review complete.";
    }
    return "This will save all pending changes and mark your review complete.";
  }
  return "This will mark your review complete.";
}

export default function SubmitReviewButton({
  className,
  hasPendingChanges,
  disabled,
  finalized,
  loading,
  size,
  pendingChangeSummary,
  onFinalize,
  onSubmitAllAndFinalize,
}: Readonly<Props>) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const confirm = useConfirmDialog();
  // Completing a review endorses the proposal content at that point in time. A
  // later proposal edit therefore opens the review again until the reviewer
  // saves that edit and completes the new version.
  const hasCompletedCurrentReview = Boolean(finalized && !hasPendingChanges);
  const buttonLabel = getButtonLabel({
    finalized: hasCompletedCurrentReview,
    hasPendingChanges,
  });

  const handleFinalize = async (submitPendingItems = false) => {
    const changesAreItemized = Boolean(submitPendingItems && pendingChangeSummary?.descriptors.length);
    const action = getFinalizeAction({
      submitPendingItems,
      hasPendingChanges,
      changesAreItemized,
    });

    const response = await confirm({
      title: "Review Complete?",
      // The combined action uses the same structured change list as Save, so reviewers can verify the
      // exact field/value transitions before they commit the save and completion together.
      message:
        changesAreItemized && pendingChangeSummary ? (
          <>
            {action}
            <ProposalChangeSummaryList
              heading={pendingChangeSummary.heading}
              descriptors={pendingChangeSummary.descriptors}
            />
          </>
        ) : (
          action
        ),
    });

    if (response === ConfirmResponse.Cancel) return;

    if (submitPendingItems) {
      onSubmitAllAndFinalize();
      return;
    }

    onFinalize();
  };

  const isButtonDisabled = Boolean(disabled || hasCompletedCurrentReview);

  const handlePrimaryClick = async () => {
    if (loading) return;
    // A completed review with nothing pending has nothing left to do; it used to stay clickable so the
    // reviewer could push their held comments, which they no longer have.
    if (hasCompletedCurrentReview) return;

    await handleFinalize(hasPendingChanges);
  };

  return (
    <SplitButtonContainer className={className}>
      <ButtonGroup
        disableElevation
        variant="contained"
        ref={anchorRef}
        aria-label="submit review button"
        size={size}
        sx={{ width: "100%" }}
      >
        <SubmitReviewActionButton
          onClick={handlePrimaryClick}
          startIcon={<GradingIcon />}
          variant="contained"
          size={size}
          loading={!!loading}
          disabled={isButtonDisabled}
        >
          {renderButtonLabel(buttonLabel)}
        </SubmitReviewActionButton>
      </ButtonGroup>
    </SplitButtonContainer>
  );
}
